import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  PageShell,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@/components/ui';
import { friendlyMessage } from '@/lib/errors';
import { COLLECTION_MALLS, COLLECTION_MALL_LABELS } from '../productCollection/collectionMalls';
import {
  ProductCollectionMallTabs,
  emptyCollectionByMall,
  type ProductCollectionByMall,
} from '../productCollection/ProductCollectionMallTabs';
import { builtInTemplates, type CollectionTemplate } from './templateModel';
import {
  useCollectionTemplatesQuery,
  useDeleteCollectionTemplateMutation,
  useSaveCollectionTemplateMutation,
} from './useCollectionTemplates';

/** 몰별 항목 수 요약(국내 5 · 일문 5 …). */
const summary = (malls: ProductCollectionByMall) =>
  COLLECTION_MALLS.map((m) => `${COLLECTION_MALL_LABELS[m].replace('몰', '')} ${malls[m].fields.length}`).join(' · ');

const TemplateFormDialog = ({
  initial,
  readOnly,
  onClose,
}: {
  initial: CollectionTemplate | null;
  readOnly: boolean;
  onClose: () => void;
}) => {
  const save = useSaveCollectionTemplateMutation();
  const [name, setName] = useState(initial?.name ?? '');
  const [malls, setMalls] = useState<ProductCollectionByMall>(initial?.malls ?? emptyCollectionByMall());
  const canSave = !readOnly && name.trim().length > 0 && COLLECTION_MALLS.some((m) => malls[m].fields.some((f) => f.label.trim()));

  const submit = () =>
    save.mutate(
      { ...(initial && !initial.builtIn ? { templateId: initial.templateId } : {}), name, malls },
      {
        onSuccess: () => {
          toast.success('템플릿을 저장했습니다.');
          onClose();
        },
      },
    );

  return (
    <Dialog open onOpenChange={(o) => (o ? undefined : onClose())}>
      <DialogContent className="max-w-5xl" data-testid="collection-template-dialog">
        <DialogHeader>
          <DialogTitle>{readOnly ? '기본 템플릿 보기' : initial ? '추가입력 템플릿 수정' : '추가입력 템플릿 등록'}</DialogTitle>
          <DialogDescription>
            템플릿 이름은 관리자만 봅니다. 몰(국내·일문·중문·영문)마다 항목을 따로 정하면, 상품에서 불러올 때 4개 몰에 한 번에 채워집니다.
          </DialogDescription>
        </DialogHeader>
        <div className="flex max-h-[65vh] flex-col gap-3 overflow-y-auto">
          <label className="grid gap-1 text-xs text-[color:var(--content-muted)]">
            템플릿 이름 (관리자용)
            <Input value={name} onChange={(e) => setName(e.target.value)} disabled={readOnly} maxLength={100} placeholder="예: 대면 이벤트용" data-testid="collection-template-name" />
          </label>
          <ProductCollectionMallTabs
            value={malls}
            disabled={readOnly}
            withTemplateBar={false}
            onChange={(mall, next) => setMalls((prev) => ({ ...prev, [mall]: next }))}
          />
          {save.error ? (
            <p role="alert" className="text-sm" style={{ color: 'var(--status-danger-fg)' }}>
              {friendlyMessage(save.error, '템플릿을 저장하지 못했습니다.')}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            닫기
          </Button>
          {readOnly ? null : (
            <Button variant="cta" size="sm" disabled={!canSave || save.isPending} onClick={submit} data-testid="collection-template-save">
              저장
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * QA-563 추가입력·동의 템플릿 관리 — 상품정보고시 템플릿처럼 자주 쓰는 추가입력 묶음(4몰)을 등록해 두고 상품 폼에서 불러온다.
 * 기본 템플릿(대면·영상통화)은 코드에 내장돼 늘 보이고, 고쳐 쓰려면 [복사해서 등록]. 저장은 BE API 반영 후 가능.
 */
export const CollectionTemplateListPage = () => {
  const query = useCollectionTemplatesQuery();
  const del = useDeleteCollectionTemplateMutation();
  const notReady = query.data === null;
  const [editing, setEditing] = useState<{ tpl: CollectionTemplate | null; readOnly: boolean } | null>(null);
  const rows: CollectionTemplate[] = [...builtInTemplates(), ...(query.data ?? [])];

  return (
    <PageShell
      title="추가입력 템플릿"
      actions={
        <Button size="sm" variant="cta" disabled={notReady} onClick={() => setEditing({ tpl: null, readOnly: false })} data-testid="collection-template-new">
          템플릿 등록
        </Button>
      }
    >
      <p className="mb-3 text-[12px] text-[color:var(--content-muted)]">
        상품 등록·수정의 추가입력·동의 항목 위 [템플릿 불러오기]에서 고르면 4개 몰에 한 번에 채워집니다. 템플릿 이름은 고객에게 보이지 않습니다.
      </p>
      {notReady ? (
        <p className="mb-3 rounded-md p-3 text-[13px]" style={{ background: 'var(--status-warn-bg)', color: 'var(--status-warn-fg)' }} data-testid="collection-template-not-ready">
          템플릿 저장은 서버 반영 후 사용할 수 있습니다. 지금은 기본 템플릿(대면·영상통화)만 불러올 수 있습니다.
        </p>
      ) : null}
      {query.isPending ? (
        <Skeleton className="h-[160px] w-full" />
      ) : (
        <Table data-testid="collection-template-table">
          <TableHeader style={{ background: 'var(--surface-1)' }}>
            <TableRow>
              <TableHead scope="col">템플릿 이름</TableHead>
              <TableHead scope="col">몰별 항목 수</TableHead>
              <TableHead scope="col">관리</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((t) => (
              <TableRow key={t.templateId} data-testid="collection-template-row">
                <TableCell>{t.name}</TableCell>
                <TableCell className="text-[12px] tabular-nums text-[color:var(--content-muted)]">{summary(t.malls)}</TableCell>
                <TableCell>
                  <div className="flex gap-1.5">
                    {t.builtIn ? (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setEditing({ tpl: t, readOnly: true })}>
                          보기
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={notReady}
                          onClick={() => setEditing({ tpl: { ...t, name: t.name.replace(' (기본)', ''), builtIn: true }, readOnly: false })}
                        >
                          복사해서 등록
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setEditing({ tpl: t, readOnly: false })}>
                          수정
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={del.isPending}
                          onClick={() => {
                            if (!window.confirm(`"${t.name}" 템플릿을 삭제할까요? 이미 적용한 상품의 항목은 그대로 남습니다.`)) return;
                            del.mutate(t.templateId, {
                              onSuccess: () => toast.success('삭제했습니다.'),
                              onError: (e) => toast.error(friendlyMessage(e, '삭제하지 못했습니다.')),
                            });
                          }}
                        >
                          삭제
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {editing ? <TemplateFormDialog initial={editing.tpl} readOnly={editing.readOnly} onClose={() => setEditing(null)} /> : null}
    </PageShell>
  );
};
